import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Accordion, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';

function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching services data from API...');
      
      const API_URL = `${baseURL}/services-page.php`;
      
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      console.log('Services API response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Services API response data:', data);
      
      if (data.success) {
        setServices(data.services || []);
      } else {
        throw new Error(data.message || 'Failed to load services data');
      }
      
    } catch (err) {
      console.error('Error fetching services data:', err);
      setError(`Error loading services: ${err.message}`);
      
      // Use fallback data if API fails
      setServices(getFallbackServices());
    } finally {
      setLoading(false);
    }
  };

  // Fallback data in case API fails completely
  const getFallbackServices = () => [
    {
      id: 1,
      name: "Supply of Laboratory Equipment & Instruments",
      description: "We supply a broad portfolio of modern and high-performance analytical instruments sourced from globally trusted manufacturers.",
      details: [
        "Chromatography Systems: HPLC, GC, Ion Chromatography, and related accessories.",
        "Spectroscopy Instruments: UV-Vis, FTIR, AAS, ICP-OES, ICP-MS, and fluorescence spectrometers.",
        "Water & Environmental Analysis Equipment: pH meters, conductivity meters, BOD/COD analyzers, turbidity meters, and water testing kits.",
        "General Laboratory Equipment: centrifuges, balances, ovens, incubators, shakers, and sample preparation systems.",
        "Consumables & Accessories: columns, vials, cuvettes, filters, reagents, and calibration standards."
      ],
      icon: "fas fa-microscope"
    },
    {
      id: 2,
      name: "Installation, Calibration & Validation",
      description: "Professional installation, calibration, and validation services to ensure all instruments perform accurately and efficiently.",
      details: [
        "Compliance with global laboratory standards",
        "Precision calibration services",
        "Method validation support",
        "Documentation and certification"
      ],
      icon: "fas fa-cogs"
    },
    {
      id: 3,
      name: "Preventive Maintenance & Repairs",
      description: "Cost-effective maintenance programs and on-demand repair services to ensure continuity and reliability.",
      details: [
        "Preventive maintenance programs",
        "Equipment life extension",
        "Minimized downtime",
        "On-demand repair services"
      ],
      icon: "fas fa-tools"
    },
    {
      id: 4,
      name: "Laboratory Design & Setup Consultancy",
      description: "Custom laboratory design and setup solutions from planning to commissioning.",
      details: [
        "Custom laboratory planning",
        "Workflow optimization",
        "Safety compliance",
        "International standards adherence"
      ],
      icon: "fas fa-drafting-compass"
    },
    {
      id: 5,
      name: "Training & Capacity Building",
      description: "Specialized training to enhance technical expertise and maximize equipment performance.",
      details: [
        "Application Training: practical operation and analytical technique optimization.",
        "Hardware Troubleshooting: identifying and resolving technical issues efficiently.",
        "Software & Systems Training: data analysis, method development, and system integration."
      ],
      icon: "fas fa-chalkboard-teacher"
    },
    {
      id: 6,
      name: "Supply of Laboratory Furniture & Accessories",
      description: "Durable, ergonomic, and customizable laboratory furniture and safety equipment.",
      details: [
        "Ergonomic laboratory benches",
        "Fume hoods and safety equipment",
        "Storage solutions",
        "Customizable designs"
      ],
      icon: "fas fa-chair"
    },
    {
      id: 7,
      name: "After-Sales Technical Support",
      description: "Strong after-sales service with remote and on-site technical support.",
      details: [
        "Remote technical support",
        "On-site assistance",
        "Spare parts supply",
        "Continuous customer support"
      ],
      icon: "fas fa-headset"
    },
    {
      id: 8,
      name: "Quality Assurance & Compliance Support",
      description: "Assistance in meeting international quality standards with method validation and calibration guidance.",
      details: [
        "ISO/IEC 17025 compliance support",
        "Method validation guidance",
        "Calibration best practices",
        "Documentation assistance"
      ],
      icon: "fas fa-award"
    }
  ];

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" variant="primary" className="mb-3">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Loading services from server...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="mb-5">
        <Col>
          <h1 className="text-center royal-blue-text emerald-green-border mb-4">Our Services</h1>
          <p className="lead text-center">
            Complete range of laboratory products, analytical instruments, and professional services 
            designed to support scientific excellence, operational efficiency, and reliable results.
          </p>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="warning" dismissible onClose={() => setError(null)}>
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
              <div className="mt-2">
                <small className="text-muted d-block">Showing fallback content</small>
                <Button variant="outline-warning" size="sm" onClick={fetchServices}>
                  Try Again
                </Button>
              </div>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Services Grid - Loaded from backend */}
      <Row className="mb-5">
        {services.map((service) => (
          <Col lg={6} className="mb-4" key={service.id}>
            <Card className="service-card h-100">
              <Card.Body>
                <div className="d-flex align-items-start mb-3">
                  {service.icon && (
                    <div className="royal-blue-text me-3">
                      <i className={`${service.icon} fa-2x`}></i>
                    </div>
                  )}
                  <div>
                    <Card.Title className="royal-blue-text h5 mb-1">
                      {service.name}
                    </Card.Title>
                  </div>
                </div>
                <Card.Text>{service.description}</Card.Text>
                
                {service.details && service.details.length > 0 && (
                  <Accordion>
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>Service Details</Accordion.Header>
                      <Accordion.Body>
                        <ul className="mb-0">
                          {service.details.map((detail, idx) => (
                            <li key={idx}>{detail}</li>
                          ))}
                        </ul>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                )}
              </Card.Body>
              <Card.Footer className="bg-transparent border-0 pt-0">
                <Button as={Link} to="/booking" variant="primary" size="sm">
                  <i className="fas fa-calendar-check me-2"></i>
                  Book This Service
                </Button>
              </Card.Footer>
            </Card>
          </Col>
        ))}
      </Row>

      {/* CTA Section */}
      <Row className="text-center">
        <Col>
          <div className="bg-light rounded p-5">
            <h3 className="royal-blue-text mb-3">Need a Custom Solution?</h3>
            <p className="mb-4">
              We understand that every laboratory has unique requirements. Contact us for tailored solutions 
              that meet your specific analytical challenges.
            </p>
            <Button as={Link} to="/contact" variant="primary" size="lg" className="me-3">
              <i className="fas fa-envelope me-2"></i>
              Contact Us
            </Button>
            <Button as={Link} to="/booking" variant="outline-primary" size="lg">
              <i className="fas fa-calendar me-2"></i>
              Book Consultation
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default Services;